/** RootView — web mirror of `Sources/Views/RootView.swift`.
 * A four-tab shell (Factory / Brain / Chat / Notifications), all driven by the
 * store via AppEnvironment. The Factory tab carries a badge with the pending
 * approvals count; Notifications badges the event count. */

import { useState } from 'react';
import { useAppState, useEnv } from '../app/context';
import { FactoryView } from './FactoryView';
import { BrainView } from './BrainView';
import { ChatView } from './ChatView';
import { NotificationsView } from './NotificationsView';
import { SetupView } from './SetupView';

type TabId = 'factory' | 'brain' | 'chat' | 'notifications' | 'setup';

const TABS: Array<{ id: TabId; label: string; icon: string }> = [
  { id: 'factory', label: 'Factory', icon: '⚙︎' },
  { id: 'brain', label: 'Brain', icon: '🧠' },
  { id: 'chat', label: 'Chat', icon: '💬' },
  { id: 'notifications', label: 'Notifications', icon: '🔔' },
  { id: 'setup', label: 'Setup', icon: '🔌' },
];

export function RootView() {
  const env = useEnv();
  const state = useAppState();
  const [tab, setTab] = useState<TabId>('factory');

  const badges: Partial<Record<TabId, number>> = {
    factory: state.snapshot.approvals.length,
    notifications: state.snapshot.events.length,
  };

  const title = TABS.find((t) => t.id === tab)!.label;

  return (
    <div className="app">
      <header className="nav-bar">
        <span className="nav-bar__title">{title}</span>
        <div className="nav-bar__actions">
          <span className="badge-chip" title="Active data backend">
            {state.backend === 'mock' ? '● Mock data' : '● Supabase'}
          </span>
          {state.backend === 'supabase' ? (
            <button
              className="signout-chip"
              onClick={() => void env.signOut()}
              title="Sign out (clears your saved Brain token)"
            >
              Sign out
            </button>
          ) : null}
        </div>
      </header>

      {state.lastError ? <div className="banner banner--error">⚠ {state.lastError}</div> : null}

      <main className="app__content">
        {tab === 'factory' ? <FactoryView /> : null}
        {tab === 'brain' ? <BrainView /> : null}
        {tab === 'chat' ? <ChatView /> : null}
        {tab === 'notifications' ? <NotificationsView /> : null}
        {tab === 'setup' ? <SetupView /> : null}
        <div style={{ height: 24 }} />
      </main>

      <nav className="tab-bar">
        {TABS.map((t) => {
          const badge = badges[t.id];
          return (
            <button
              key={t.id}
              className="tab-bar__item"
              aria-selected={tab === t.id}
              onClick={() => {
                setTab(t.id);
                if (t.id === tab) void env.refresh();
              }}
            >
              <span className="tab-bar__icon" aria-hidden>
                {t.icon}
              </span>
              {t.label}
              {badge ? <span className="tab-bar__badge">{badge}</span> : null}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
